import { cva } from 'class-variance-authority';

/**
 * The trigger, layered onto `IconButton`'s `3xs` Muted cell — Figma's `Size=Tiny, Type=Muted,
 * iconOnly=Yes`, which is what the `Info Button` set (1945:115418) instances.
 *
 * THE RESTING GLYPH TAKES THE COLOUR OF THE TEXT IT SITS IN. The set draws `foreground/secondary`
 * at rest because it is drawn beside a field label, and the contrast rule says an icon bound to a
 * label inherits the label's contrast. `currentColor` makes that structural rather than copied: in
 * a field it follows the label through open and disabled, and beside a heading it takes the
 * heading's colour. Hover and pressed keep Muted's own `foreground/primary` steps.
 *
 * FOCUS AND OPEN READ THE PRESSED SLOT, which for Muted is `foreground/primary` — the set draws
 * the Focus and Active cells at primary. `data-popup-open` is Base UI's trigger attribute, so a
 * tooltip opened by tap keeps the glyph at primary for as long as it shows.
 *
 * THE GLYPH IS 14px, NOT THE TINY CELL'S 12px. At 12px it read small beside a 14px label, and
 * smaller still once the label floats. The button stays 16px so the label row keeps its height,
 * which leaves 1px round the glyph. Scoped here rather than on `3xs`, so every other Tiny button
 * keeps Figma's `x3` glyph.
 *
 * ⚠️ AHEAD OF FIGMA, knowingly. The set still draws 12px: Figma sizes the icon slot on the shared
 * Button's Tiny cells, so an instance can swap the glyph but not resize it without detaching.
 * Recorded as a DS gap.
 *
 * THE HIT AREA IS 24px ROUND A 16px BUTTON ABOVE `sm`, AND 44px ON PHONES. The `after` layer here
 * is transparent and inside the button, so it takes the button's pointer events; 24px clears WCAG
 * 2.5.8. Below `sm` the `IconButton` underneath adds `Button.recipe`'s own invisible 44px `before`
 * layer, which meets the product rules' floor on touch. The trade-off is that on a phone the
 * target reaches into the field's value line, so a tap there can open the tooltip rather than
 * place the caret.
 */
export const infoButtonRecipe = cva([
  "after:absolute after:-inset-1 after:content-['']",
  '[--ds-btn-icon-size:--spacing(3.5)]',
  '[--ds-btn-fg:currentColor]',
  'focus-visible:[--ds-btn-fg:var(--ds-btn-fg-pressed)]',
  'data-[popup-open]:[--ds-btn-fg:var(--ds-btn-fg-pressed)]',
]);

/**
 * The wrapper a field's label becomes when it carries an info button. It takes the field's own
 * label recipe — position, float, colour, type — and lays the label and the button out in a row
 * with Figma's `x1` gap.
 *
 * `overflow-visible!` undoes the label recipe's `truncate` on the ROW, which would otherwise clip
 * the button's focus ring and hit area. Truncation moves to the text instead. It needs the `!`
 * because `truncate` and `overflow-*` are different tailwind-merge groups, so `cn` keeps both and
 * stylesheet order would decide.
 *
 * The row lets pointer events through and the two children take them back, so the 4px gap is not
 * a dead strip over the control.
 */
export const fieldLabelRowRecipe = cva(['flex items-center gap-1 overflow-visible! pointer-events-none']);

/**
 * The label text inside that row. `cursor-[inherit]` keeps the field's own cursor, which its label
 * recipe sets on the row: browsers draw a `<label>` with the arrow by default, so without it the
 * label read as plain text over a field you could type in.
 */
export const fieldLabelTextRecipe = cva(['min-w-0 truncate pointer-events-auto cursor-[inherit]']);

/**
 * Keeps the button 16px while the label floats.
 *
 * Every field floats its label by SCALING it to 0.857143 (14px → 12px) rather than changing its
 * font size, and the row inherits that scale. So this wrapper scales back by the inverse, from its
 * left edge, on the label's own clock. The product of the two stays within 0.6% of 1 through the
 * transition, and the gap to the label ends up 3.4px rather than 4px when floated — kept rather
 * than chased, since the alternative is animating font size.
 *
 * The selectors mirror the label recipes' float triggers. Text Area also floats on
 * `:placeholder-shown`, and the others must not, since a Text Input with a placeholder keeps its
 * label down. `data-has-tags` is Tag Input's: its label floats once a tag lands, with nothing typed.
 */
export const fieldLabelInfoRecipe = cva(
  [
    'pointer-events-auto inline-flex shrink-0 origin-left scale-100',
    'transition-[scale] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
    'group-data-[filled]/ti:scale-[1.166667] group-data-[focused]/ti:scale-[1.166667]',
    'group-data-[has-tags]/ti:scale-[1.166667]',
  ],
  {
    variants: {
      floatsOnPlaceholder: {
        true: 'group-has-[:placeholder-shown]/ti:scale-[1.166667]',
        false: '',
      },
    },
    defaultVariants: { floatsOnPlaceholder: false },
  }
);
